import { useState, useEffect } from 'preact/hooks';
import { getCreditsHistory, type CreditHistoryRow } from '../api/billing';
import { formatCredits } from '../state/creditsStore';
import { PortalTaxLine } from './PortalTaxLine';

// ════════════════════════════════════════════════════════════════════════════
// PortalHistoryTab — read-only renderer of the append-only credit_ledger for the
// current user (valko-credits-platform Phase 3).
//
// NO wallet FSM here — this is read-only fetch state (UsageTab precedent), server
// -owned append-only ledger data, not a client transition surface. Rows: topup /
// bonus / grant_* (positive), hold / release / burn / expiry (zero-net or
// negative). Compensating/storno rows surface as negative entries — the portal
// NEVER initiates a refund (no write path).
//
// The backend read endpoint (`GET /me/credits/history`) may not exist yet; on any
// error the tab renders a graceful "coming soon" state rather than a hard failure.
//
// Stripe Tax P3 (voice-assistant issue #2176): a paid top-up row also carries the Stripe-computed tax
// (`e.tax`, read by the backend from `stripe_tax_record`) — rendered by <PortalTaxLine/> as "Paid EUR 40.00 ·
// incl. VAT EUR 6.39 (19%) · Billing country: DE". Purely additive: rows with no `tax` (legacy purchases,
// bonus/burn rows, an older backend) render exactly as before. Pinned by setpoint-evals/stripe-tax-p3/SE-02.
// ════════════════════════════════════════════════════════════════════════════

const REASON_LABELS: Record<string, string> = {
  topup: 'Top-up',
  bonus: 'Bonus',
  grant_signup: 'Signup grant',
  grant_daily: 'Daily grant',
  hold: 'Reserved',
  release: 'Released',
  burn: 'Used',
  expiry: 'Expired',
  refund_clawback: 'Refunded',
};

function reasonLabel(reason: string): string {
  return REASON_LABELS[reason] ?? reason.replace(/_/g, ' ');
}

/** Signed centicredits → "+15", "-4.2" credits string. */
function signedCredits(deltaCenti: number): string {
  if (deltaCenti === 0) return '0';
  const sign = deltaCenti > 0 ? '+' : '-';
  return `${sign}${formatCredits(Math.abs(deltaCenti))}`;
}

/** Optional human note captured in the row's meta jsonb (e.g. gated feature key). */
function rowNote(meta: Record<string, unknown> | null): string | null {
  const n = meta?.note ?? meta?.featureKey ?? meta?.reason;
  return typeof n === 'string' ? n : null;
}

export function PortalHistoryTab() {
  const [entries, setEntries] = useState<CreditHistoryRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCreditsHistory()
      .then((r) => {
        if (cancelled) return;
        setEntries(r.rows ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        setUnavailable(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div class="admin-content" style={{ padding: 24, textAlign: 'center', color: 'var(--text-dim, #5a5a7e)' }}>
        Loading history…
      </div>
    );
  }

  if (unavailable) {
    return (
      <div class="admin-content" style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary, #8888aa)' }}>
        Credit history isn't available yet.
        <div style={{ fontSize: 12, color: 'var(--text-dim, #5a5a7e)', marginTop: 4 }}>
          Check back soon — your ledger will appear here.
        </div>
      </div>
    );
  }

  if (!entries || entries.length === 0) {
    return (
      <div class="admin-content" style={{ padding: 24, textAlign: 'center', color: 'var(--text-dim, #5a5a7e)' }}>
        No credit activity yet.
      </div>
    );
  }

  return (
    <div class="admin-content" style={{ padding: '8px 0' }}>
      <div style={{ overflowX: 'auto', padding: '0 8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-subtle, #2a2a44)' }}>
              <th style={{ padding: '8px 6px', textAlign: 'left', color: 'var(--text-secondary, #8888aa)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>When</th>
              <th style={{ padding: '8px 6px', textAlign: 'left', color: 'var(--text-secondary, #8888aa)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>Activity</th>
              <th style={{ padding: '8px 6px', textAlign: 'right', color: 'var(--text-secondary, #8888aa)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>Credits</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => {
              const positive = e.centicredits > 0;
              const zero = e.centicredits === 0;
              const note = rowNote(e.meta);
              return (
                <tr
                  key={e.id}
                  data-testid="portal-history-row"
                  data-entry-id={e.id}
                  data-entry-type={e.entryType}
                  style={{ borderBottom: '1px solid var(--border-subtle, #2a2a44)' }}
                >
                  <td style={{ padding: '8px 6px', color: 'var(--text-dim, #5a5a7e)', fontSize: 11, whiteSpace: 'nowrap' }}>
                    {new Date(e.createdAt).toLocaleString()}
                  </td>
                  <td style={{ padding: '8px 6px', color: 'var(--text-primary, #c8c8e8)' }}>
                    {reasonLabel(e.entryType)}
                    {note && (
                      <div style={{ fontSize: 11, color: 'var(--text-dim, #5a5a7e)', marginTop: 2 }}>{note}</div>
                    )}
                    <PortalTaxLine tax={e.tax} paidEur={e.eurGross} />
                  </td>
                  <td
                    style={{
                      padding: '8px 6px',
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                      fontWeight: 600,
                      color: zero
                        ? 'var(--text-dim, #5a5a7e)'
                        : positive
                          ? 'var(--accent-green, #4caf50)'
                          : 'var(--text-secondary, #8888aa)',
                    }}
                  >
                    {signedCredits(e.centicredits)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
